/** @fileoverview Resolves the references of a Workday source into the fixed values a read sends. */

import { dayOf } from './effectiveDates';
import type {
  FabrikRecordKeyRefValue,
  FabrikWorkdayDataSource,
  FabrikWorkdayIdPair,
  FabrikWorkdayValue,
} from '../../types/apps';

/**
 * Tells whether a Workday value or criteria is a ref.
 * @param value a Workday value, ID pair or switch
 */
export const isRef = (value: unknown): value is FabrikRecordKeyRefValue =>
  typeof value === 'object' && value !== null && (value as { type?: unknown }).type === 'ref';

/**
 * Lists the refs a Workday source reads: in record IDs, criteria values and the as-of day.
 * @param source the Workday source
 */
export const workdayRefsOf = (source: FabrikWorkdayDataSource): FabrikRecordKeyRefValue[] => {
  const { references = [], criteria = {}, asOf } = source;
  return [
    ...references.map(({ value }) => value),
    ...Object.values(criteria).map((value) =>
      typeof value === 'object' && !isRef(value) ? value.value : value
    ),
    asOf,
  ].filter(isRef);
};

/**
 * Resolves the references of a Workday source: record IDs and criteria values become text, and
 * the as-of day becomes `YYYY-MM-DD`. An empty ID or criteria value stops the read; an empty
 * as-of day is left out.
 * @param source the Workday source
 * @param readRef reads the value of a ref, the same way row filter values are read
 */
export const resolveWorkdaySource = (
  source: FabrikWorkdayDataSource,
  readRef: (ref: FabrikRecordKeyRefValue) => unknown
): FabrikWorkdayDataSource => {
  /**
   * Resolves one ID or criteria value into text.
   * @param value fixed text or a ref
   */
  const resolveText = (value: FabrikWorkdayValue): string => {
    const resolved = isRef(value) ? readRef(value) : value;
    if (
      (typeof resolved !== 'string' && typeof resolved !== 'number') ||
      !String(resolved).trim()
    ) {
      throw new Error('Required row data for this Workday read is unavailable.');
    }
    return String(resolved);
  };
  /**
   * Resolves the value of an ID pair.
   * @param pair the ID type and value
   */
  const resolvePair = ({ type, value }: FabrikWorkdayIdPair): FabrikWorkdayIdPair => ({
    type,
    value: resolveText(value),
  });
  const { references, criteria, asOf } = source;
  const asOfDay =
    asOf === undefined ? undefined : dayOf(typeof asOf === 'string' ? asOf : readRef(asOf));
  return {
    ...source,
    ...(references ? { references: references.map(resolvePair) } : {}),
    ...(criteria
      ? {
          criteria: Object.fromEntries(
            Object.entries(criteria).map(([name, value]) => [
              name,
              typeof value === 'boolean'
                ? value
                : typeof value === 'object' && !isRef(value)
                ? resolvePair(value)
                : resolveText(value),
            ])
          ),
        }
      : {}),
    asOf: asOfDay,
  };
};

/**
 * Builds the body of the Workday read route for a resolved source: a page by `top` and `skip`,
 * or only the row count.
 * @param source the Workday source, its references resolved
 * @param options the page to read, or `count: true`
 */
export const buildWorkdayQueryBody = (
  source: FabrikWorkdayDataSource,
  options: { count: true } | { top: number; skip: number }
) => {
  const { entity, values, references, criteria, asOf, responseGroup } = source;
  const readFields = {
    entity,
    ...(references ? { references } : {}),
    ...(criteria ? { criteria } : {}),
    ...(typeof asOf === 'string' ? { asOfDate: asOf } : {}),
    ...(responseGroup ? { responseGroup } : {}),
  };
  return 'count' in options
    ? { ...readFields, count: true as const }
    : { ...readFields, values, top: options.top, skip: options.skip };
};
